import { supabase, supabaseAdmin } from '../lib/supabaseClient';

export type OrderType = 'delivery' | 'takeaway' | 'dine_in';
export type ProductTypeOption = 'pizza' | 'drink' | 'sauce' | 'snack' | 'combo';
export type OrderTypeDiscountType = 'fixed' | 'percentage';

interface OrderTypeDiscountRow {
  id: string;
  order_type: OrderType;
  product_type: ProductTypeOption | null;
  product_id: string | null;
  product_ids?: string[] | null;
  discount_type: OrderTypeDiscountType | null;
  discount_amount: number;
  is_active: boolean;
  starts_at: string | null;
  ends_at: string | null;
  size_template_ids: string[] | null;
  created_at: string;
  updated_at: string;
  products?: {
    id: string;
    name: string;
    product_type: ProductTypeOption | null;
  } | {
    id: string;
    name: string;
    product_type: ProductTypeOption | null;
  }[] | null;
}

const ORDER_TYPE_DISCOUNT_SELECT = `
  id,
  order_type,
  product_type,
  product_id,
  product_ids,
  discount_type,
  discount_amount,
  is_active,
  starts_at,
  ends_at,
  size_template_ids,
  created_at,
  updated_at,
  products ( id, name, product_type )
`;

const LEGACY_ORDER_TYPE_DISCOUNT_SELECT = `
  id,
  order_type,
  product_type,
  product_id,
  discount_amount,
  is_active,
  starts_at,
  ends_at,
  size_template_ids,
  created_at,
  updated_at,
  products ( id, name, product_type )
`;

const NO_PRODUCT_IDS_ORDER_TYPE_DISCOUNT_SELECT = `
  id,
  order_type,
  product_type,
  product_id,
  discount_type,
  discount_amount,
  is_active,
  starts_at,
  ends_at,
  size_template_ids,
  created_at,
  updated_at,
  products ( id, name, product_type )
`;

const isMissingColumn = (error: unknown, columnName: string) => {
  const maybeError = error as { code?: string; message?: string } | null | undefined;
  return (
    maybeError?.code === 'PGRST204' &&
    (maybeError.message ?? '').includes(`'${columnName}'`)
  );
};

const isMissingDiscountTypeColumn = (error: unknown) => isMissingColumn(error, 'discount_type');
const isMissingProductIdsColumn = (error: unknown) => isMissingColumn(error, 'product_ids');

const getDiscountTypeMigrationError = () =>
  new Error(
    'Yüzde indirim için veritabanı migrasyonu uygulanmalı: order_type_discounts.discount_type kolonu henüz API şema önbelleğinde yok.',
  );

const getProductListMigrationError = () =>
  new Error(
    'Toplu ürün listesi için veritabanı migrasyonu uygulanmalı: order_type_discounts.product_ids kolonu henüz API şema önbelleğinde yok.',
  );

export interface OrderTypeDiscountRecord {
  id: string;
  orderType: OrderType;
  productType: ProductTypeOption | null;
  productId: string | null;
  productIds: string[];
  productName: string | null;
  sizeTemplateIds: string[];
  discountType: OrderTypeDiscountType;
  discountAmount: number;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const mapOrderTypeDiscount = (row: OrderTypeDiscountRow): OrderTypeDiscountRecord => {
  const product = Array.isArray(row.products) ? row.products[0] ?? null : row.products ?? null;
  const productIds = Array.isArray(row.product_ids)
    ? row.product_ids.filter((value): value is string => Boolean(value))
    : row.product_id
      ? [row.product_id]
      : [];

  return {
    id: row.id,
    orderType: row.order_type,
    productType:
      row.product_id || productIds.length > 0
        ? product?.product_type ?? row.product_type ?? null
        : row.product_type ?? null,
    productId: row.product_id,
    productIds,
    productName: product?.name ?? null,
    sizeTemplateIds: Array.isArray(row.size_template_ids)
      ? row.size_template_ids.filter((value): value is string => Boolean(value))
      : [],
    discountType: row.discount_type ?? 'fixed',
    discountAmount: Number(row.discount_amount ?? 0),
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    isActive: Boolean(row.is_active),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export async function listOrderTypeDiscounts(): Promise<OrderTypeDiscountRecord[]> {
  const getSelect = (includeDiscountType: boolean, includeProductIds: boolean) => {
    if (!includeDiscountType) {
      return LEGACY_ORDER_TYPE_DISCOUNT_SELECT;
    }
    return includeProductIds ? ORDER_TYPE_DISCOUNT_SELECT : NO_PRODUCT_IDS_ORDER_TYPE_DISCOUNT_SELECT;
  };

  const runQuery = (includeDiscountType: boolean, includeProductIds: boolean) =>
    (supabase as any)
      .from('order_type_discounts')
      .select(getSelect(includeDiscountType, includeProductIds))
      .order('order_type', { ascending: true })
      .order('created_at', { ascending: false });

  let { data, error } = await runQuery(true, true);

  if (isMissingProductIdsColumn(error)) {
    const noProductIdsResult = await runQuery(true, false);
    data = noProductIdsResult.data;
    error = noProductIdsResult.error;
  }

  if (isMissingDiscountTypeColumn(error)) {
    const legacyResult = await runQuery(false, false);
    data = legacyResult.data;
    error = legacyResult.error;
  }

  if (error) {
    throw error;
  }

  return ((data ?? []) as unknown as OrderTypeDiscountRow[]).map(mapOrderTypeDiscount);
}

export interface UpsertOrderTypeDiscountInput {
  id?: string;
  orderType: OrderType;
  productType?: ProductTypeOption | null;
  productId?: string | null;
  productIds?: string[] | null;
  sizeTemplateIds?: string[] | null;
  discountType: OrderTypeDiscountType;
  discountAmount: number;
  startsAt?: string | null;
  endsAt?: string | null;
  isActive?: boolean;
}

export async function upsertOrderTypeDiscount(
  input: UpsertOrderTypeDiscountInput,
): Promise<OrderTypeDiscountRecord> {
  const client = supabaseAdmin ?? supabase;

  const normalizedProductIds = (input.productIds ?? []).filter(Boolean);
  const singleProductId = input.productId ?? (normalizedProductIds.length === 1 ? normalizedProductIds[0] : null);
  const bulkProductIds = normalizedProductIds.length > 1 ? normalizedProductIds : null;

  const buildPayload = (includeDiscountType: boolean, includeProductIds: boolean) => ({
    order_type: input.orderType,
    product_type: singleProductId || bulkProductIds ? null : input.productType ?? null,
    product_id: singleProductId,
    ...(includeProductIds ? { product_ids: bulkProductIds } : {}),
    ...(includeDiscountType ? { discount_type: input.discountType } : {}),
    discount_amount: input.discountAmount,
    starts_at: input.startsAt ?? null,
    ends_at: input.endsAt ?? null,
    size_template_ids: input.sizeTemplateIds?.length ? input.sizeTemplateIds : null,
    is_active: input.isActive ?? true,
  });

  const getSelect = (includeDiscountType: boolean, includeProductIds: boolean) => {
    if (!includeDiscountType) {
      return LEGACY_ORDER_TYPE_DISCOUNT_SELECT;
    }
    return includeProductIds ? ORDER_TYPE_DISCOUNT_SELECT : NO_PRODUCT_IDS_ORDER_TYPE_DISCOUNT_SELECT;
  };

  const runUpsert = (includeDiscountType: boolean, includeProductIds: boolean) => {
    const payload = buildPayload(includeDiscountType, includeProductIds);
    return (client as any)
      .from('order_type_discounts')
      .upsert(input.id ? [{ ...payload, id: input.id }] : [payload], {
        onConflict: 'id',
      })
      .select(getSelect(includeDiscountType, includeProductIds))
      .single();
  };

  let { data, error } = await runUpsert(true, true);

  if (isMissingProductIdsColumn(error)) {
    if (bulkProductIds) {
      throw getProductListMigrationError();
    }

    const noProductIdsResult = await runUpsert(true, false);
    data = noProductIdsResult.data;
    error = noProductIdsResult.error;
  }

  if (isMissingDiscountTypeColumn(error)) {
    if (input.discountType === 'percentage') {
      throw getDiscountTypeMigrationError();
    }

    const legacyResult = await runUpsert(false, false);
    data = legacyResult.data;
    error = legacyResult.error;
  }

  if (error) {
    throw error;
  }

  return mapOrderTypeDiscount(data as unknown as OrderTypeDiscountRow);
}

export async function deleteOrderTypeDiscount(discountId: string) {
  const client = supabaseAdmin ?? supabase;
  const { data, error } = await client
    .from('order_type_discounts')
    .delete()
    .eq('id', discountId)
    .select('id')
    .maybeSingle();
  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error('İndirim kuralı silinemedi. Yetki/RLS nedeniyle kayıt değişmemiş olabilir.');
  }
}

export async function setOrderTypeDiscountActive(discountId: string, isActive: boolean) {
  const client = supabaseAdmin ?? supabase;
  const { data, error } = await client
    .from('order_type_discounts')
    .update({ is_active: isActive, updated_at: new Date().toISOString() })
    .eq('id', discountId)
    .select('id')
    .maybeSingle();
  if (error) {
    throw error;
  }
  if (!data) {
    throw new Error('İndirim kuralı güncellenemedi. Yetki/RLS nedeniyle kayıt değişmemiş olabilir.');
  }
}
